import { Button } from '@mui/material'
import { useCallback, useRef } from 'react'
import { Fade, InformationModal } from 'utoniq-ui'

import { Maybe, PostDetailQuery } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  CommentList,
  CommentListProps,
  PostDetailBox,
  PostDetailBoxSkeleton,
  ReplyFormRef,
  ReplyFormValues,
} from '@/components/organisms'
import { NotFound } from '@/components/pages/Errors/NotFound'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  isLoggedIn: boolean
  hasNextPage: boolean
  user: Maybe<PostDetailQuery['user']>
  post: Maybe<PostDetailQuery['post']>
  isMembershipPersonalInformationRequired: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onReply: (values: ReplyFormValues) => Promise<void>
  onLike: (isLike: boolean, likeCount: number) => Promise<void>
  onDeleteReply: CommentListProps['onDeleteComment']
  onReportUser: CommentListProps['onReportUser']
  onBlockUser: CommentListProps['onBlockUser']
  onLikeOrUnlikeReply: CommentListProps['onLikeOrUnlikeComment']
  onReportArtist?: (
    postId: string,
    artistId: string,
    accountId: string
  ) => Promise<void>
  onClickShare: () => Promise<void>
}

export const PostDetailTemplate: Template<State, Action> = ({
  state,
  onReply,
  onLike,
  onDeleteReply,
  onReportUser,
  onBlockUser,
  onReportArtist,
  onLikeOrUnlikeReply,
  onFetchMore,
  onClickShare,
}) => {
  const { routerNames, routerPages, generatePath, router } =
    useRouter<{ postId: string }>()
  const replyFormRef = useRef<ReplyFormRef>(null)
  const { showMessage } = useMessage()
  const { t } = useTranslation('postDetail')

  const handleReportArtist = useCallback(async () => {
    if (!onReportArtist) return

    await onReportArtist(
      state.post?.id ?? '',
      state.post?.artist?.id ?? '',
      state.post?.artist?.formattedAccountId ?? ''
    )
  }, [
    onReportArtist,
    state.post?.artist?.formattedAccountId,
    state.post?.artist?.id,
    state.post?.id,
  ])

  const handleBlockArtist = useCallback(async () => {
    await onBlockUser(state.post?.artistId ?? '')
    showMessage(t('blockArtistSuccess'), 'success')
    router.push(
      generatePath(routerPages.ArtistTop, {
        artistId: state.post?.artistId,
      })
    )
  }, [
    generatePath,
    onBlockUser,
    router,
    routerPages.ArtistTop,
    showMessage,
    state.post?.artistId,
    t,
  ])

  if (!state.loading && !state.post) {
    return <NotFound useNextRouter={false} />
  }
  // ユーザーが加入しているメンバーシップよりも上位プランであるかどうか
  const isGraterPlan = !state?.post?.isAccessible && state?.post?.planId
  return (
    <ArtistThemeProvider artistId={state.post?.artistId}>
      <BaseLayout
        navigation={
          state.post?.isAccessible
            ? {
                type: 'replyForm',
                props: {
                  ref: replyFormRef,
                  onReply,
                  userId: state.user?.id ?? '',
                },
              }
            : undefined
        }
        header={{
          back: true,
          defaultBackPage:
            state.post?.artistId &&
            generatePath(routerPages.ArtistTop, {
              artistId: state.post?.artistId,
            }),
        }}
      >
        <Head
          title={[routerNames.PostDetail, state.post?.artist.displayName]}
        />
        {state.loading && <PostDetailBoxSkeleton />}
        {!state.loading && state?.post && (
          <Fade>
            <PostDetailBox
              userId={state.user?.id}
              post={state.post}
              isMembershipPersonalInformationRequired={
                state.isMembershipPersonalInformationRequired
              }
              onLike={() => {
                if (!state.post) return
                onLike(!!state?.post.isLike, state?.post.likeCount)
              }}
              onReply={() => replyFormRef.current?.focus()}
              onBlockArtist={handleBlockArtist}
              onReportArtist={handleReportArtist}
              enableEmbedHtml={true}
              onClickShare={onClickShare}
            />
          </Fade>
        )}
        {!state?.loading && !state?.post?.isAccessible && (
          <InformationModal
            onClose={() =>
              router.goBack(
                generatePath(routerPages.ArtistTop, {
                  artistId: state?.post?.artist.id,
                })
              )
            }
            open={true}
            title={
              isGraterPlan
                ? t('viewMemberInfoTitleWithDiff')
                : t('viewMemberInfoTitle')
            }
            content={
              isGraterPlan
                ? t('viewMemberInfoDescWithDiff', {
                    planTitle: state?.post?.planTitle,
                  })
                : t('viewMemberInfoDesc', {
                    displayName: state?.post?.artist.displayName,
                  })
            }
            cancelText={t('close')}
            buttons={
              <Button
                size="large"
                color="primary"
                sx={{ minWidth: 212 }}
                href={
                  state?.post?.artist?.id
                    ? generatePath(routerPages.ArtistMembership, {
                        artistId: state?.post.artist.id,
                      })
                    : undefined
                }
              >
                {t('seeDetail')}
              </Button>
            }
          />
        )}
        {state.post?.isAccessible && (
          <CommentList
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            commentCount={state.post?.replyCount}
            comments={state.post?.replies ?? null}
            user={state.user ?? null}
            onFetchMore={onFetchMore}
            onDeleteComment={onDeleteReply}
            onReportUser={onReportUser}
            onBlockUser={onBlockUser}
            showReaction
            onLikeOrUnlikeComment={onLikeOrUnlikeReply}
            hiddenReply
            onClickReply={() => {}}
            onReplyToReply={() => {}}
          />
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
